import { useState } from 'react';
import {
  Button,
  Group,
  Stack,
  Text,
  FileInput,
  Alert,
} from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useUploadSingleSourceDocument } from '../hooks/useSingleSource';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

interface SingleSourceDocumentUploadProps {
  formId: string;
  currentFileName?: string;
  onUploadSuccess?: () => void;
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Invalid file type. Only PDF, DOC, and DOCX files are allowed.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'File size exceeds 10MB limit.';
  }
  return null;
}

export function SingleSourceDocumentUpload({
  formId,
  currentFileName,
  onUploadSuccess,
}: SingleSourceDocumentUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const uploadDocument = useUploadSingleSourceDocument();

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setValidationError(null);
    if (selectedFile) {
      const error = validateFile(selectedFile);
      setValidationError(error);
    }
  };

  const handleUpload = () => {
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = (e.target?.result as string).split(',')[1];
      uploadDocument.mutate(
        {
          id: formId,
          data: { fileName: file.name, base64File: base64 },
        },
        {
          onSuccess: () => {
            setFile(null);
            onUploadSuccess?.();
          },
        }
      );
    };
    reader.readAsDataURL(file);
  };

  return (
    <Stack gap="sm">
      <FileInput
        label={currentFileName ? 'Replace document' : 'Upload document'}
        placeholder="Select PDF, DOC, or DOCX file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        value={file}
        onChange={handleFileChange}
        clearable
      />
      {validationError && (
        <Alert color="red" variant="light">
          {validationError}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button
          leftSection={<IconUpload size={16} />}
          onClick={handleUpload}
          loading={uploadDocument.isPending}
          disabled={!file || !!validationError}
        >
          {currentFileName ? 'Replace Document' : 'Upload Document'}
        </Button>
      </Group>
    </Stack>
  );
}
